import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../src/Context/AuthContext";
import EmptyHeader from '../../Components/Shared/EmptyHeader';

const ProfilePage = () => {
    const navigate = useNavigate();

    //State Variables
    const { user, logout } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);
    const [company, setCompany] = useState(null);
    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    //ENV Variables
    const companyName = import.meta.env.VITE_COMPANY_NAME;
    const apiURL = import.meta.env.VITE_API;

    //Set Site Title
    document.title = `${companyName} - User Profile`;

    useEffect(() => {
        if (!user?.userID) return;

        const fetchProfile = async () => {
            try {
                const res = await fetch(`${apiURL}/account/getUserInfo?userID=${user.userID}`, {
                    method: 'GET',
                    credentials: 'include'
                });

                if (res.status === 401) {
                    logout();
                    navigate('/login');
                    throw new Error("unauthorized");
                }

                if (res.status === 403) {
                    navigate('/home');
                    throw new Error("forbidden");
                }

                if (res.status === 404) {
                    setProfile(null);
                    return;
                }

                if (!res.ok) {
                    const text = await res.text();
                    console.error(`GetUserInfo failed: ${res.status} ${res.statusText}\n${text}`);
                    return;
                }

                const data = await res.json();
                setProfile(data);
            } catch (err) {
                console.error("Error fetching user profile:", err);
            }
        };

        const fetchCompany = async () => {
            try {
                const res = await fetch(`${apiURL}/shared/getCompanyInfo`, {
                    method: 'GET',
                    credentials: 'include'
                });

                if (res.status === 401) {
                    logout();
                    navigate('/login');
                    throw new Error("unauthorized");
                }

                if (res.status === 403) {
                    navigate('/home');
                    throw new Error("forbidden");
                }

                if (res.status === 404) {
                    setCompany(null);
                    return;
                }

                if (!res.ok) {
                    const text = await res.text();
                    console.error(`GetCompanyInfo failed: ${res.status} ${res.statusText}\n${text}`);
                    return;
                }

                const data = await res.json();
                setCompany(data);
            } catch (err) {
                console.error("Error fetching company info:", err);
            }
        };

        fetchProfile();
        fetchCompany();
    }, [user?.userID, apiURL, logout, navigate]);

    if (!user) return <p>Loading profile...</p>;

    const regularDisplayAddress = company ? [
        company.addrName,
        company.addr1,
        company.addr2,
        `${company.city}, ${company.state} ${company.zip}`
    ].filter(Boolean).join('\n') : "";

    const overnightDisplayAddress = company ? [
        company.overnightAddrName,
        company.overnightAddr1,
        company.overnightAddr2,
        `${company.overnightCity}, ${company.overnightState} ${company.overnightZip}`
    ].filter(Boolean).join('\n') : "";

    // Prefer hydrated profile, fall back to context user
    const model = profile ?? user;

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (passwords.newPassword !== passwords.confirmPassword) {
            alert("New passwords do not match.");
            return;
        }

        try {
            setLoading(true);
            const res = await fetch(`${apiURL}/account/update-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userID: user.userID,
                    currentPassword: passwords.currentPassword,
                    newPassword: passwords.newPassword,
                }),
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) throw new Error("Failed to change password");

            alert("Password successfully changed!");
            setIsModalOpen(false);
            setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
        } catch (err) {
            console.error(err);
            alert("Error changing password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile-container">
            <EmptyHeader />
            <div className="profile-wrapper">
                {/* --- USER INFO CARD --- */}
                <div className="profile-card">
                    <h3 className="profile-header">User Info</h3>
                    <div className="profile-content">
                        <div className="profile-avatar">
                            <div className="avatar-circle">
                                {model.firstName?.[0]?.toUpperCase() ||
                                    model.username?.[0]?.toUpperCase() ||
                                    "U"}
                            </div>
                        </div>

                        <div className="profile-details">
                            <div className="profile-row">
                                <span className="profile-label">First Name:</span>
                                <span className="profile-value">{model.firstName || "N/A"}</span>
                            </div>

                            <div className="profile-row">
                                <span className="profile-label">Last Name:</span>
                                <span className="profile-value">{model.lastName || "N/A"}</span>
                            </div>

                            <div className="profile-row">
                                <span className="profile-label">Email:</span>
                                <span className="profile-value">{model.email || "N/A"}</span>
                            </div>

                            {model.role === "4" && (
                                <div className="profile-row">
                                    <span className="profile-label">Date of Birth:</span>
                                    <span className="profile-value">
                                        {model.birthDate
                                            ? new Date(model.birthDate).toLocaleDateString()
                                            : "N/A"}
                                    </span>
                                </div>
                            )}

                            {(model.role === "3" || model.role === "4" || model.role === "5") && (
                                <div className="profile-row">
                                    <span className="profile-label">Last 4 SSN:</span>
                                    <span className="profile-value">{model.lastFour || "N/A"}</span>
                                </div>
                            )}

                            <div className="profile-row">
                                <span className="profile-label">Username:</span>
                                <span className="profile-value">{model.userName}</span>
                            </div>

                            {model.role === "1" && (
                                <div className="profile-row">
                                    <span className="profile-label">Agent Code:</span>
                                    <span className="profile-value">{model.agentCode || "N/A"}</span>
                                </div>
                            )}

                            {user.username !== "amrportaladmin" && (
                                <div className="profile-actions">
                                    <button
                                        className="baseButton"
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        Change Password
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* ---- NOTE ABOUT OTHER CHANGES ---- */}
                    <div className="profile-note">
                        If you need to update any other information (such as your email,
                        mailing address, or phone number), please contact Customer Service
                        {company?.phone ? ` at ${company.phone}.` : "."}
                    </div>
                </div>

                {/*--- COMPANY INFO CARD --- */}
                {company && (
                    <div className="profile-card">
                        <h3 className="profile-header">Company Info</h3>
                        <div className="profile-details">
                            <div className="profile-row">
                                <span className="profile-label">Phone:</span>
                                <span className="profile-value">{company.phone}</span>
                            </div>

                            <div className="profile-row">
                                <span className="profile-label">Fax:</span>
                                <span className="profile-value">{company.fax}</span>
                            </div>

                            <div className="profile-row">
                                <span className="profile-label">Overnight Mail:</span>
                                <span className="profile-value company-address">
                                    {overnightDisplayAddress.split('\n').map((line, idx) => (
                                        <span key={idx}>{line}<br /></span>
                                    ))}
                                </span>
                            </div>

                            <div className="profile-row">
                                <span className="profile-label">Regular Mail:</span>
                                <span className="profile-value company-address">
                                    {regularDisplayAddress.split('\n').map((line, idx) => (
                                        <span key={idx}>{line}<br /></span>
                                    ))}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* --- PASSWORD MODAL --- */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div
                        className="modal-content small"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h3>Change Password</h3>
                            <button
                                className="close-button"
                                onClick={() => setIsModalOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <form className="change-password-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Current Password</label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    className="filterInput"
                                    value={passwords.currentPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    className="filterInput"
                                    value={passwords.newPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    className="filterInput"
                                    value={passwords.confirmPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="baseButton"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="baseButton secondary"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;
